import { api } from './apiClient';

// Types
interface Opportunity {
  id: string;
  title: string;
  description: string;
  value: number;
  probability: number;
  stage: string;
  expectedCloseDate: string;
  customerId: string;
  customerName: string;
  assignedTo: string;
  status: 'open' | 'won' | 'lost' | 'qualified';
}

interface Quotation {
  id: string;
  quotationNumber: string;
  customerId: string;
  customerName: string;
  items: QuotationItem[];
  subtotal: number;
  tax: number;
  total: number;
  validUntil: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
}

interface QuotationItem {
  id: string;
  productId: string;
  productName: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

export const salesService = {
  // Opportunities
  getOpportunities: async (params?: any): Promise<Opportunity[]> => {
    return api.get<Opportunity[]>('/sales/opportunities', { params });
  },

  getOpportunity: async (id: string): Promise<Opportunity> => {
    return api.get<Opportunity>(`/sales/opportunities/${id}`);
  },

  createOpportunity: async (opportunity: Omit<Opportunity, 'id'>): Promise<Opportunity> => {
    return api.post<Opportunity>('/sales/opportunities', opportunity);
  },

  updateOpportunity: async (id: string, opportunity: Partial<Opportunity>): Promise<Opportunity> => {
    // Controller nests the result under an `opportunity` key (see
    // src/controllers/sales.js updateOpportunity).
    const response = await api.put<{ opportunity: Opportunity }>(`/sales/opportunities/${id}`, opportunity);
    return response.opportunity;
  },

  deleteOpportunity: async (id: string): Promise<void> => {
    return api.delete(`/sales/opportunities/${id}`);
  },

  // Quotations
  getQuotations: async (params?: any): Promise<Quotation[]> => {
    return api.get<Quotation[]>('/sales/quotations', { params });
  },

  getQuotation: async (id: string): Promise<Quotation> => {
    return api.get<Quotation>(`/sales/quotations/${id}`);
  },

  createQuotation: async (quotation: Omit<Quotation, 'id'>): Promise<Quotation> => {
    return api.post<Quotation>('/sales/quotations', quotation);
  },

  updateQuotation: async (id: string, quotation: Partial<Quotation>): Promise<Quotation> => {
    // Controller nests the result under a `quotation` key (see
    // src/controllers/sales.js updateQuotation).
    const response = await api.put<{ quotation: Quotation }>(`/sales/quotations/${id}`, quotation);
    return response.quotation;
  },

  deleteQuotation: async (id: string): Promise<void> => {
    return api.delete(`/sales/quotations/${id}`);
  },

  // Reports
  getSalesReport: async (startDate: string, endDate: string): Promise<any> => {
    return api.get('/sales/reports', {
      params: { startDate, endDate },
    });
  },

  getPipelineReport: async (): Promise<any> => {
    return api.get('/sales/reports/pipeline');
  },

  getPerformanceReport: async (userId?: string): Promise<any> => {
    return api.get('/sales/reports/performance', {
      params: { userId },
    });
  },
};